import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { salaryComponentsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type {
  SalaryCalculationMethod,
  SalaryComponent,
  SalaryComponentInput,
  SalaryComponentType,
} from '@/types/hr';
import { formatRate } from '@/utils/format';
import { Badge, Money, SalaryStatusBadge } from './shared';

const METHODS: { value: SalaryCalculationMethod; label: string; hint: string }[] = [
  { value: 'FIXED', label: 'A fixed amount', hint: 'The same figure every month.' },
  {
    value: 'PERCENTAGE_OF_BASIC',
    label: 'A percentage of basic',
    hint: 'Scales with the person’s basic salary.',
  },
  {
    value: 'PERCENTAGE_OF_GROSS',
    label: 'A percentage of gross',
    hint: 'Worked out after the other earnings, so two of these never compound.',
  },
];

const EMPTY: SalaryComponentInput = {
  code: '',
  name: '',
  description: '',
  componentType: 'EARNING',
  calculationMethod: 'FIXED',
  defaultValue: null,
  taxable: true,
  statutory: false,
  affectsGross: true,
  displayOrder: 0,
};

/**
 * The named parts a salary is built from.
 *
 * <p>Retired, never deleted: payslips going back years name the components they
 * were made of, and a statutory one cannot be retired at all — an income tax
 * deduction is not something a payroll clerk negotiates away.
 */
export function SalaryComponentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.SALARY_COMPONENT_MANAGE);

  const [editing, setEditing] = useState<SalaryComponent | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SalaryComponentInput>(EMPTY);

  const components = useQuery({
    queryKey: queryKeys.salaryComponents.list(false),
    queryFn: () => salaryComponentsApi.list(false),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.salaryComponents.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.salaryStructures.all });
  };

  const save = useMutation({
    mutationFn: () =>
      editing === null
        ? salaryComponentsApi.create(form)
        : salaryComponentsApi.update(editing.id, form),
    onSuccess: (component) => {
      toast.success(`${component.name} saved`);
      close();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      salaryComponentsApi.setStatus(id, status),
    onSuccess: (component) => {
      toast.success(`${component.name} is now ${component.status.toLowerCase()}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const open = (component: SalaryComponent | null) => {
    setEditing(component);
    setCreating(component === null);
    setForm(
      component === null
        ? EMPTY
        : {
            code: component.code,
            name: component.name,
            description: component.description ?? '',
            componentType: component.componentType,
            calculationMethod: component.calculationMethod,
            defaultValue: component.defaultValue ?? null,
            taxable: component.taxable,
            statutory: component.statutory,
            affectsGross: component.affectsGross,
            displayOrder: component.displayOrder,
          },
    );
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY);
  };

  const columns: Column<SalaryComponent>[] = [
    {
      key: 'name',
      header: 'Component',
      render: (row) => (
        <span>
          <span className="block text-sm font-medium text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Direction',
      render: (row) => (
        <Badge tone={row.componentType === 'EARNING' ? 'success' : 'danger'}>
          {row.componentType === 'EARNING' ? 'Earning' : 'Deduction'}
        </Badge>
      ),
    },
    {
      key: 'value',
      header: 'Default',
      className: 'text-right',
      render: (row) =>
        row.defaultValue === undefined || row.defaultValue === null ? (
          <span className="text-subtle">—</span>
        ) : row.calculationMethod === 'FIXED' ? (
          <Money value={row.defaultValue} />
        ) : (
          <span className="tabular-nums text-ink">{formatRate(row.defaultValue)}</span>
        ),
    },
    {
      key: 'method',
      header: 'Worked out as',
      render: (row) => (
        <span className="text-[13px] text-muted">
          {METHODS.find((method) => method.value === row.calculationMethod)?.label}
        </span>
      ),
    },
    {
      key: 'flags',
      header: '',
      render: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.statutory && <Badge tone="warning">Statutory</Badge>}
          {!row.taxable && <Badge tone="neutral">Untaxed</Badge>}
          {row.componentType === 'EARNING' && !row.affectsGross && (
            <Badge tone="neutral">Outside gross</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'usage',
      header: 'In packages',
      className: 'text-right',
      render: (row) => <span className="text-[13px] text-muted">{row.structureCount}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => <SalaryStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            {/* A statutory component cannot be retired; the server refuses it. */}
            {!row.statutory && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setStatus.mutate({
                    id: row.id,
                    status: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                  })
                }
              >
                {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
              </Button>
            )}
          </span>
        ) : null,
    },
  ];

  const method = METHODS.find((entry) => entry.value === form.calculationMethod);

  return (
    <div>
      <PageHeader
        title="Salary Components"
        description="The parts a salary is built from: allowances, deductions and the rules for each."
        icon={<Percent className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Add a component</Button> : undefined}
      />

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={components.data}
          rowKey={(row) => row.id}
          loading={components.isPending}
          error={components.error}
          onRetry={() => void components.refetch()}
          emptyTitle="No salary components"
          emptyDescription="A workable set is created the first time this page is opened."
        />
      </Card>

      {(editing !== null || creating) && (
        <Modal
          open
          onClose={close}
          title={editing === null ? 'Add a salary component' : `Edit ${editing.name}`}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Code"
                required
                disabled={editing !== null}
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
                placeholder="HOUSE_RENT"
                hint={editing !== null ? 'Fixed once created' : undefined}
              />
              <TextField
                label="Name"
                required
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="House Rent Allowance"
              />
            </div>

            <SelectField
              label="Direction"
              required
              // An earning cannot become a deduction: every payslip already
              // produced was calculated with it on one side of the line.
              disabled={editing !== null}
              value={form.componentType}
              onChange={(event) =>
                setForm({
                  ...form,
                  componentType: event.target.value as SalaryComponentType,
                  affectsGross: event.target.value === 'EARNING',
                })
              }
              hint={editing !== null ? 'Cannot change once created' : undefined}
            >
              <option value="EARNING">An earning — adds to pay</option>
              <option value="DEDUCTION">A deduction — takes away from pay</option>
            </SelectField>

            <SelectField
              label="Worked out as"
              required
              value={form.calculationMethod ?? 'FIXED'}
              onChange={(event) =>
                setForm({ ...form, calculationMethod: event.target.value as SalaryCalculationMethod })
              }
              hint={method?.hint}
            >
              {METHODS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </SelectField>

            <TextField
              label={form.calculationMethod === 'FIXED' ? 'Amount' : 'Percentage'}
              type="number"
              step="0.01"
              min="0"
              value={form.defaultValue ?? ''}
              onChange={(event) =>
                setForm({
                  ...form,
                  defaultValue: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              hint="A package can override this for its own staff"
            />

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={form.taxable ?? true}
                onChange={(event) => setForm({ ...form, taxable: event.target.checked })}
                className="mt-0.5 h-4 w-4 border-line"
              />
              <span>
                Counts towards taxable pay
                <span className="block text-[12px] text-muted">
                  Independent of direction — a medical allowance is an untaxed earning.
                </span>
              </span>
            </label>

            {form.componentType === 'EARNING' && (
              <label className="flex items-start gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={form.affectsGross ?? true}
                  onChange={(event) => setForm({ ...form, affectsGross: event.target.checked })}
                  className="mt-0.5 h-4 w-4 border-line"
                />
                <span>
                  Part of gross pay
                  <span className="block text-[12px] text-muted">
                    Off for a reimbursement: paid, but not remuneration, so percentage-of-gross
                    components are not taken of it.
                  </span>
                </span>
              </label>
            )}

            <TextField
              label="Display order"
              type="number"
              min="0"
              value={form.displayOrder ?? 0}
              onChange={(event) => setForm({ ...form, displayOrder: Number(event.target.value) })}
              hint="Also the order components are applied in"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
